import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Button, Box, Alert, Link, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { LoginDto, AuthResponseDto } from '../authentication.types';
import { useLogin } from '../hooks/useAuth';

interface LoginFormProps {
  onAuthResponse?: (response: AuthResponseDto) => void;
  onForgotPassword?: () => void;
}

interface LoginFormData extends LoginDto {
  rememberMe?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onAuthResponse, onForgotPassword }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [isHandlingFirstLogin, setIsHandlingFirstLogin] = React.useState(false);
  const loginMutation = useLogin();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<LoginFormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsHandlingFirstLogin(false);

    try {
      // Map form data to Cognito types
      const response = await loginMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });

      // Handle the authentication response
      if (response.isFirstLogin) {
        setIsHandlingFirstLogin(true);
      }

      if (onAuthResponse) {
        onAuthResponse(response);
      }

      // Only reset form if login was successful (not first login)
      if (response.isSignedIn) {
        reset();
      }
    } catch (error) {
      console.error('Login failed:', error);
      setIsHandlingFirstLogin(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
        {loginMutation.error && !isHandlingFirstLogin && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500,
              },
            }}
          >
            {loginMutation.error.message || 'Login failed. Please try again.'}
          </Alert>
        )}

        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Email Address"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              margin="normal"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.05)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: 'Password is required',
            // Note: No pattern validation for login - allows AWS Cognito temporary passwords
            // with any special characters (e.g., ^ in '^75$ks1MuQkC')
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors.password?.message}
              margin="normal"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.05)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        {onForgotPassword && (
          <Box textAlign="right" sx={{ mt: 2, mb: 3 }}>
            <Link
              component="button"
              type="button"
              onClick={onForgotPassword}
              underline="hover"
              variant="body2"
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                color: 'primary.main',
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
            >
              Forgot password?
            </Link>
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={!isValid || loginMutation.isPending}
          sx={{
            mt: 2,
            mb: 3,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '1rem',
            boxShadow: '0 4px 12px rgba(255, 90, 71, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(255, 90, 71, 0.4)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              boxShadow: 'none',
              transform: 'none',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {loginMutation.isPending ? 'Signing In...' : 'Sign In'}
        </Button>
      </Box>
    </motion.div>
  );
};
